import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const PaymentCalculatorPage = () => {
  const [calcMode, setCalcMode] = useState('payment');
  const [loanAmount, setLoanAmount] = useState('200000');
  const [interestRate, setInterestRate] = useState('5');
  const [loanTerm, setLoanTerm] = useState('30');
  const [termUnit, setTermUnit] = useState('years');
  const [monthlyPayment, setMonthlyPayment] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const P = parseFloat(loanAmount) || 0;
    const r = (parseFloat(interestRate) || 0) / 100 / 12;
    const n = termUnit === 'years' ? parseFloat(loanTerm) * 12 : parseFloat(loanTerm);
    const payment = parseFloat(monthlyPayment) || 0;

    if (calcMode === 'payment') {
      const M = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      const total = M * n;
      const interest = total - P;
      setResult({ type: 'payment', payment: M, total, interest, principal: P, months: n });
    } else if (calcMode === 'amount') {
      const maxLoan = payment * (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n));
      const total = payment * n;
      const interest = total - maxLoan;
      setResult({ type: 'amount', payment, maxLoan, total, interest, months: n });
    } else if (calcMode === 'term') {
      const months = Math.log(payment / (payment - P * r)) / Math.log(1 + r);
      const total = payment * months;
      const interest = total - P;
      setResult({ type: 'term', payment, months: Math.ceil(months), years: (months / 12).toFixed(1), total, interest, principal: P });
    }
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link><span className="mx-2">›</span>
            <span className="text-gray-400">Payment Calculator</span>
          </nav>
          <h1 className="text-3xl font-bold text-white mb-6">Payment Calculator</h1>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex flex-wrap border-b border-[#2a2a2a] mb-6 gap-1">
              {[{ id: 'payment', label: 'Find Payment' }, { id: 'amount', label: 'Find Loan Amount' }, { id: 'term', label: 'Find Loan Term' }].map(m => (
                <button key={m.id} onClick={() => setCalcMode(m.id)}
                  className={`px-4 py-2 text-sm border-b-2 ${calcMode === m.id ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>{m.label}</button>
              ))}
            </div>

            <div className="space-y-4">
              {(calcMode === 'payment' || calcMode === 'term') && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Loan Amount</label>
                  <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                    <Input type="number" value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)}
                      className="w-36 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Interest Rate</label>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.01" value={interestRate} onChange={(e) => setInterestRate(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" /><span className="text-gray-400">%</span>
                </div>
              </div>

              {(calcMode === 'payment' || calcMode === 'amount') && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Loan Term</label>
                  <div className="flex items-center gap-2">
                    <Input type="number" value={loanTerm} onChange={(e) => setLoanTerm(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <select value={termUnit} onChange={(e) => setTermUnit(e.target.value)}
                      className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                      <option value="years">years</option><option value="months">months</option>
                    </select>
                  </div>
                </div>
              )}

              {(calcMode === 'amount' || calcMode === 'term') && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Monthly Payment</label>
                  <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                    <Input type="number" value={monthlyPayment} onChange={(e) => setMonthlyPayment(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
              )}
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="text-center mb-4">
                  {result.type === 'payment' && <><p className="text-gray-400">Monthly Payment</p><p className="text-4xl font-bold text-green-400">{formatCurrency(result.payment)}</p></>}
                  {result.type === 'amount' && <><p className="text-gray-400">Max Loan Amount</p><p className="text-4xl font-bold text-green-400">{formatCurrency(result.maxLoan)}</p></>}
                  {result.type === 'term' && <><p className="text-gray-400">Loan Term</p><p className="text-4xl font-bold text-green-400">{result.months} months ({result.years} years)</p></>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg"><p className="text-gray-400 text-sm">Total Payments</p><p className="text-white font-semibold">{formatCurrency(result.total)}</p></div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg"><p className="text-gray-400 text-sm">Total Interest</p><p className="text-cyan-400 font-semibold">{formatCurrency(result.interest)}</p></div>
                </div>
              </div>
            )}
          </div>
          <AdInContent />
          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Loan', path: '/loan-calculator' }, { name: 'Mortgage', path: '/mortgage-calculator' }, { name: 'Amortization', path: '/amortization-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentCalculatorPage;
